/**
 * The site's public, login-free pages (W8-RIDER-SAFETY-CONSENT, D440): a rider's live-trip link (`/v/<token>`, R-02), a hotel guest's ride link (`/v/g/<token>`, D850) and
 * the legal texts in force (`/legal/<country>/<audience>/<kind>`, X-02). The pure mapping lives in
 * `../src/public-pages.ts`; this renders it with the site's tokens (no colour literal) and its base-language copy.
 */
import { type ReactElement, useEffect, useMemo, useState } from 'react';
import wordmarkInk from '@vello/brand/assets/wordmark/svg/vello-wordmark-ink.svg';
import { publicApiUrl, type PublicRoute } from '../src/public-pages.js';
import { TripShareView } from './TripShareView';
import { createCopy, selectSiteCatalog, type Copy } from './i18n';

async function getJson(url: string): Promise<{ status: number; body: unknown }> {
  try {
    const res = await fetch(url, { credentials: 'omit', cache: 'no-store' });
    let body: unknown;
    try {
      body = await res.json();
    } catch {
      body = undefined;
    }
    return { status: res.status, body };
  } catch {
    return { status: 0, body: undefined };
  }
}

function Legal({ route, apiOrigin, t }: { route: Extract<PublicRoute, { kind: 'legal' }>; apiOrigin: string | undefined; t: Copy }): ReactElement {
  const [doc, setDoc] = useState<{ title: string; body: string; version: string } | 'missing' | undefined>(undefined);
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const { status, body } = await getJson(publicApiUrl(apiOrigin, route));
      if (cancelled) return;
      const o = typeof body === 'object' && body !== null ? (body as Record<string, unknown>) : {};
      setDoc(status === 200 && typeof o.title === 'string' && typeof o.body === 'string' && typeof o.version === 'string' ? { title: o.title, body: o.body, version: o.version } : 'missing');
    })();
    return () => {
      cancelled = true;
    };
  }, [apiOrigin, route]);
  if (doc === undefined) return <p className="notice-body">{t('site.trip.loading')}</p>;
  if (doc === 'missing') return <p className="notice-body" data-testid="legal-missing">{t('site.legal.notPublished')}</p>;
  return (
    <article data-testid="legal-document">
      <h1 className="notice-title">{doc.title}</h1>
      <p className="notice-body">{t('site.legal.version', { version: doc.version })}</p>
      {doc.body.split(/\n{2,}/).map((para, i) => (
        <p key={i} className="notice-body">{para}</p>
      ))}
    </article>
  );
}

export function PublicPage({ route, apiOrigin }: { route: PublicRoute; apiOrigin: string | undefined }): ReactElement {
  const locale = typeof navigator !== 'undefined' ? navigator.language : 'en-US';
  const t = useMemo(() => createCopy(selectSiteCatalog(locale)), [locale]);
  useEffect(() => {
    if (typeof document === 'undefined') return;
    // A shared link is private to whoever holds it: keep it out of search indexes and out of the Referer header.
    const meta = document.createElement('meta');
    meta.name = 'robots';
    meta.content = 'noindex, nofollow';
    const ref = document.createElement('meta');
    ref.name = 'referrer';
    ref.content = 'no-referrer';
    document.head.append(meta, ref);
    return () => {
      meta.remove();
      ref.remove();
    };
  }, []);
  // A shared ride is a page of its own (owner, 2026-11-04): the map and a sheet, none of the site's navigation or footer.
  if (route.kind === 'trip-share' || route.kind === 'guest-ride') return <TripShareView route={route} apiOrigin={apiOrigin} t={t} locale={locale} />;
  return (
    <div className="bootstrap">
      <header className="nav">
        <div className="nav-inner">
          <img className="nav-wordmark" src={wordmarkInk} alt="Vello" />
        </div>
      </header>
      <main className="bootstrap-main">
        <section className="notice" role="status">
          <Legal route={route} apiOrigin={apiOrigin} t={t} />
        </section>
      </main>
      <footer className="bootstrap-footer">
        <span className="tnum">{t('site.footer.rights')}</span>
      </footer>
    </div>
  );
}
